import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Settings, Save, Loader2, Info, Key, Mail, Calendar, Image, RefreshCw, CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { isAdmin, token } = useAuth();
  const [settings, setSettings] = useState({
    ms_graph_client_id: '',
    ms_graph_client_secret: '',
    ms_graph_tenant_id: '',
    ms_graph_valid_until: '',
    notification_email: 'info@gb-sylt.de'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Thumbnail generation state
  const [thumbnailStatus, setThumbnailStatus] = useState(null);
  const [generatingThumbnails, setGeneratingThumbnails] = useState(false);

  // Calculate expiration status
  const expirationStatus = useMemo(() => {
    if (!settings.ms_graph_valid_until) return null;
    
    const expiryDate = new Date(settings.ms_graph_valid_until);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { type: 'error', days: Math.abs(daysUntilExpiry), message: 'abgelaufen' };
    } else if (daysUntilExpiry <= 14) {
      return { type: 'warning', days: daysUntilExpiry, message: 'läuft bald ab' };
    } else if (daysUntilExpiry <= 30) {
      return { type: 'info', days: daysUntilExpiry, message: 'Erinnerung' };
    }
    return { type: 'ok', days: daysUntilExpiry, message: 'gültig' };
  }, [settings.ms_graph_valid_until]);

  useEffect(() => {
    if (isAdmin()) {
      fetchSettings();
      fetchThumbnailStatus();
    }
  }, [isAdmin]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const fetchThumbnailStatus = async () => {
    try {
      const response = await axios.get(`${API}/images/thumbnail-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThumbnailStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch thumbnail status:', error);
    }
  };

  const handleGenerateThumbnails = async () => {
    if (generatingThumbnails) return;
    
    setGeneratingThumbnails(true);
    toast.info('Thumbnail-Generierung gestartet...');
    
    try {
      const response = await axios.post(`${API}/images/generate-thumbnails`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { processed, failed, total_without_thumbnails } = response.data;
      
      if (processed > 0) {
        toast.success(`${processed} Thumbnails erfolgreich erstellt${failed > 0 ? `, ${failed} fehlgeschlagen` : ''}`);
      } else if (total_without_thumbnails === 0) {
        toast.info('Alle Bilder haben bereits Thumbnails');
      } else {
        toast.warning(`Keine Thumbnails erstellt, ${failed} fehlgeschlagen`);
      }
      
      // Refresh status
      await fetchThumbnailStatus();
    } catch (error) {
      console.error('Thumbnail generation failed:', error);
      toast.error('Thumbnail-Generierung fehlgeschlagen');
    } finally {
      setGeneratingThumbnails(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success('Einstellungen gespeichert');
      fetchSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="text-center py-16">
        <Settings className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">Keine Berechtigung</h3>
        <p className="text-slate-500">Diese Seite ist nur für Administratoren zugänglich.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 heading-app">Einstellungen</h1>
          <p className="text-slate-500">Systemkonfiguration und API-Einstellungen</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-button">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Speichern
        </Button>
      </div>

      {/* Microsoft Graph Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Microsoft Graph API
          </CardTitle>
          <CardDescription>
            Konfiguration für automatische E-Mail-Benachrichtigungen
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Expiration Warning */}
          {expirationStatus && expirationStatus.type === 'error' && (
            <Alert className="border-red-500 bg-red-50">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <AlertTitle className="text-red-800">Client Secret abgelaufen!</AlertTitle>
              <AlertDescription className="text-red-700">
                Das Client Secret ist seit {expirationStatus.days} Tagen abgelaufen. 
                E-Mail-Benachrichtigungen funktionieren nicht mehr. Bitte erneuern Sie das Secret sofort.
              </AlertDescription>
            </Alert>
          )}
          
          {expirationStatus && expirationStatus.type === 'warning' && (
            <Alert className="border-amber-500 bg-amber-50">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <AlertTitle className="text-amber-800">Client Secret läuft bald ab!</AlertTitle>
              <AlertDescription className="text-amber-700">
                Das Client Secret läuft in {expirationStatus.days} Tagen ab. 
                Bitte erneuern Sie es rechtzeitig, um Ausfälle zu vermeiden.
              </AlertDescription>
            </Alert>
          )}

          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription>
              Die Microsoft Graph API wird verwendet, um automatische E-Mail-Benachrichtigungen zu senden, 
              wenn Gäste neue Bilder hochladen. Sie benötigen eine Azure App-Registrierung mit Mail.Send Berechtigung.
            </AlertDescription>
          </Alert>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="client_id">Client ID</Label>
              <Input
                id="client_id"
                value={settings.ms_graph_client_id}
                onChange={(e) => handleChange('ms_graph_client_id', e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                data-testid="client-id-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tenant_id">Tenant ID</Label>
              <Input
                id="tenant_id"
                value={settings.ms_graph_tenant_id}
                onChange={(e) => handleChange('ms_graph_tenant_id', e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                data-testid="tenant-id-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="client_secret">Client Secret</Label>
            <Input
              id="client_secret"
              type="password"
              value={settings.ms_graph_client_secret}
              onChange={(e) => handleChange('ms_graph_client_secret', e.target.value)}
              placeholder={settings.ms_graph_client_secret ? '••••••••' : 'Client Secret eingeben'}
              data-testid="client-secret-input"
            />
            <p className="text-xs text-slate-500">
              Lassen Sie das Feld leer, um das bestehende Secret zu behalten
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="valid_until">Gültig bis</Label>
            <div className="flex items-center gap-3">
              <Input
                id="valid_until"
                type="date"
                value={settings.ms_graph_valid_until}
                onChange={(e) => handleChange('ms_graph_valid_until', e.target.value)}
                className={`flex-1 ${expirationStatus?.type === 'error' ? 'border-red-500' : expirationStatus?.type === 'warning' ? 'border-amber-500' : ''}`}
                data-testid="valid-until-input"
              />
              {expirationStatus && (
                <div className={`px-3 py-2 rounded-md text-sm font-medium ${
                  expirationStatus.type === 'error' ? 'bg-red-100 text-red-800' :
                  expirationStatus.type === 'warning' ? 'bg-amber-100 text-amber-800' :
                  expirationStatus.type === 'info' ? 'bg-blue-100 text-blue-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {expirationStatus.type === 'error' ? `${expirationStatus.days} Tage abgelaufen` :
                   expirationStatus.type === 'ok' ? `${expirationStatus.days} Tage gültig` :
                   `${expirationStatus.days} Tage verbleibend`}
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500">
              Datum, bis wann das Client Secret gültig ist (zur Erinnerung)
            </p>
          </div>

          <div className="p-4 bg-slate-50 rounded-lg">
            <h4 className="font-medium text-slate-900 mb-2">So erhalten Sie die Zugangsdaten:</h4>
            <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
              <li>Gehen Sie zu portal.azure.com</li>
              <li>Navigieren Sie zu "Azure Active Directory" → "App-Registrierungen"</li>
              <li>Erstellen Sie eine neue Registrierung</li>
              <li>Unter "API-Berechtigungen" fügen Sie "Mail.Send" (Anwendungsberechtigung) hinzu</li>
              <li>Erteilen Sie die Administrator-Zustimmung</li>
              <li>Erstellen Sie unter "Zertifikate & Geheimnisse" ein neues Client Secret</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Email Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            E-Mail-Benachrichtigungen
          </CardTitle>
          <CardDescription>
            Empfänger für Upload-Benachrichtigungen
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="notification_email">Benachrichtigungs-E-Mail</Label>
            <Input
              id="notification_email"
              type="email"
              value={settings.notification_email}
              onChange={(e) => handleChange('notification_email', e.target.value)}
              placeholder="info@gb-sylt.de"
              data-testid="notification-email-input"
            />
            <p className="text-xs text-slate-500">
              An diese Adresse werden Benachrichtigungen über neue Uploads gesendet
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Thumbnail Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            Thumbnail-Verwaltung
          </CardTitle>
          <CardDescription>
            Vorschaubilder für die Medienbibliothek
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {thumbnailStatus && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Thumbnail-Abdeckung</span>
                <span className="font-medium">
                  {thumbnailStatus.with_thumbnails} / {thumbnailStatus.total_images} Bilder
                </span>
              </div>
              
              <Progress value={thumbnailStatus.percentage_complete} className="h-2" />
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{thumbnailStatus.with_thumbnails}</p>
                  <p className="text-xs text-slate-500">Mit Thumbnail</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{thumbnailStatus.without_thumbnails}</p>
                  <p className="text-xs text-slate-500">Ohne Thumbnail</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{thumbnailStatus.percentage_complete}%</p>
                  <p className="text-xs text-slate-500">Abdeckung</p>
                </div>
              </div>
              
              {thumbnailStatus.without_thumbnails > 0 ? (
                <Button 
                  onClick={handleGenerateThumbnails} 
                  disabled={generatingThumbnails}
                  className="w-full"
                  data-testid="generate-thumbnails-button"
                >
                  {generatingThumbnails ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Thumbnails werden erstellt...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {thumbnailStatus.without_thumbnails} fehlende Thumbnails generieren
                    </>
                  )}
                </Button>
              ) : (
                <div className="flex items-center justify-center gap-2 p-3 bg-green-50 rounded-lg text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Alle Bilder haben Thumbnails</span>
                </div>
              )}
            </div>
          )}
          
          {!thumbnailStatus && (
            <div className="flex items-center justify-center h-20">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}
          
          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription>
              Thumbnails werden automatisch beim Upload neuer Bilder erstellt. 
              Verwenden Sie diese Funktion, um fehlende Thumbnails für ältere Bilder nachträglich zu generieren.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* System Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="w-5 h-5" />
            Systeminfo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-slate-500">Version</p>
              <p className="font-medium">1.0.0</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-slate-500">Status</p>
              <p className="font-medium text-green-600">Aktiv</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-slate-500">Max. Dateigröße</p>
              <p className="font-medium">20 MB</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-slate-500">Erlaubte Formate</p>
              <p className="font-medium">JPG, PNG, HEIC</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Future Social Media Section (prepared but disabled) */}
      <Card className="opacity-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Social Media (Coming Soon)
          </CardTitle>
          <CardDescription>
            Diese Funktion wird in einer zukünftigen Version verfügbar sein
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-slate-500 text-sm">
            Hier können Sie später Social-Media-Konten verbinden und automatische Veröffentlichungen planen.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
